/**
 * PresetManager — the glue between the app's live state and the persistence
 * layer (`PresetStore`).
 *
 * `captureState()` snapshots every serializable piece of the synth:
 * transport BPM, voice settings (if available), module states, mod
 * connections. `applyState(preset)` pushes those back through the
 * appropriate setters (`Parameter.set()`, module `deserialize()`,
 * router `addConnection()`), with source tag `'preset'` so the control
 * plane can distinguish preset-driven changes from UI ones.
 *
 * Voice and Router are resolved lazily — both are created on first audio
 * unlock. Saving before that still works (captures the static parts);
 * loading before that defers the voice/router bits until those are ready.
 */

import type { ModConnection } from '@ac-slayer/synth-core';
import type { ModRouter } from '@ac-slayer/synth-core';
import type { SynthModule } from '@ac-slayer/synth-core';
import type { Transport } from '@ac-slayer/synth-core';
import type { VoiceManager, VoiceMode } from '@ac-slayer/synth-core';
import type { MacroBank } from '@ac-slayer/synth-core';
import type { NumericParameter } from '@ac-slayer/synth-core';
import {
  CURRENT_SCHEMA_VERSION,
  DEFAULT_PRESET_CATEGORY,
  mintPresetId,
  type Preset,
  type PresetCapture,
} from './schema';
import type { PresetStore } from './store';

/**
 * Minimal surface that PresetManager needs to capture and restore the
 * voice-level UI state (mode, glide, unison, spread). VoiceControls
 * implements this; tests can stub it with any object that has the same
 * shape.
 */
export interface VoiceSettingsProvider {
  getMode(): VoiceMode;
  setMode(mode: VoiceMode): void;
  readonly glide: NumericParameter;
  readonly unison: NumericParameter;
  readonly spread: NumericParameter;
}

/** What PresetManager needs from the EffectsChain to round-trip presets. */
export interface EffectsChainProvider {
  /** Snapshot every effect instance — type, id, params, bypass. */
  serialize(): Array<{
    type: string;
    id: string;
    bypass: boolean;
    params: Record<string, number | string>;
  }>;
  /** Replace the chain composition from a saved list, in order. */
  applyPreset(
    list: ReadonlyArray<{
      type: string;
      id: string;
      bypass?: boolean;
      params: Record<string, number | string>;
    }>,
  ): void;
}

export interface PresetManagerDeps {
  readonly transport: Transport;
  readonly modules: readonly SynthModule[];
  getVoiceManager(): VoiceManager | null;
  getRouter(): ModRouter | null;
  /** Optional — when present, voice settings round-trip through the UI. */
  readonly voiceSettings?: VoiceSettingsProvider;
  /** Optional — when present, macro values are saved and restored. */
  readonly macroBank?: MacroBank;
  /** Optional — when present, the post-voice effect chain order is saved
   *  and restored. Null-safe: presets without effectsOrder leave the
   *  current chain order alone. */
  readonly effectsChain?: EffectsChainProvider;
  /** Optional — when present, MIDI Learn bindings are saved with the
   *  preset and replayed on load. Decoupled via this small surface
   *  so PresetManager doesn't need to import MidiBindings directly. */
  readonly midiBindings?: MidiBindingsProvider;
  /** Optional — fired synchronously before `applyState` mutates any
   *  Parameters. Used by the undo system to checkpoint the pre-load
   *  state so loading a factory preset (or any other applyState) is
   *  one undo step away. */
  readonly beforeApply?: () => void;
}

/** Round-trip surface for the MIDI Learn store. */
export interface MidiBindingsProvider {
  serialize(): Array<{ paramRef: string; channel: number; controller: number }>;
  applyBindings(
    bindings: ReadonlyArray<{
      paramRef: string;
      channel: number;
      controller: number;
    }>,
  ): void;
}

export type PresetListener = (manager: PresetManager) => void;

export class PresetManager {
  private readonly store: PresetStore;
  private readonly deps: PresetManagerDeps;
  private readonly listeners = new Set<PresetListener>();

  constructor(deps: PresetManagerDeps, store: PresetStore) {
    this.deps = deps;
    this.store = store;
  }

  /** Snapshot the app's current state (everything persistable). */
  captureState(): PresetCapture {
    const voices = this.deps.getVoiceManager();
    const router = this.deps.getRouter();
    const vs = this.deps.voiceSettings;
    return {
      bpm: this.deps.transport.bpm.value,
      voice: {
        polyphony: voices?.polyphony ?? 8,
        // Prefer the UI's view of voice settings when available — it's the
        // source of truth for mode/glide/unison/spread. Fall back to the
        // VoiceManager's current values otherwise (e.g., in tests).
        mode: vs?.getMode() ?? voices?.mode ?? 'poly',
        glideMs: vs?.glide.value ?? voices?.glideMs ?? 0,
        unison: vs?.unison.value ?? voices?.unison ?? 1,
        unisonSpread: vs?.spread.value ?? voices?.unisonSpread ?? 15,
      },
      modules: this.deps.modules.map((m) => m.serialize()),
      modConnections: router?.listConnections() ?? [],
      ...(this.deps.macroBank
        ? { macros: this.deps.macroBank.macros.map((m) => m.value.value) }
        : {}),
      ...(this.deps.effectsChain
        ? {
            effects: this.deps.effectsChain.serialize(),
            // effectsOrder is redundant with `effects` ordering as of v4
            // but kept for older clients that don't know about v4 yet.
            effectsOrder: this.deps.effectsChain.serialize().map((e) => e.type),
          }
        : {}),
      ...(this.deps.midiBindings
        ? { midiBindings: this.deps.midiBindings.serialize() }
        : {}),
    };
  }

  /**
   * Apply a preset to the live app. Existing mod connections are torn down
   * and rebuilt from the preset; module params are pushed through
   * deserialize() with source 'preset'; transport and voice settings are
   * updated. No-op for any piece whose runtime dependency isn't ready yet.
   */
  applyState(preset: Preset): void {
    // Notify any pre-apply listeners (e.g. the undo manager) before
    // we touch a single Parameter, so they can capture the
    // pre-load state for an undo checkpoint.
    this.deps.beforeApply?.();

    // BPM first — downstream sync'd modules will recompute their rates.
    this.deps.transport.bpm.set(preset.bpm, 'preset');

    // Voice settings. Prefer the UI-backed `voiceSettings` so sliders /
    // buttons update alongside the underlying engine; fall back to direct
    // VoiceManager setters otherwise.
    const vs = this.deps.voiceSettings;
    if (vs) {
      vs.setMode(preset.voice.mode);
      // .set() cascades through the Parameter subscribe chain, which calls
      // voices.setGlideMs / setUnison / setUnisonSpread on our behalf.
      vs.glide.set(preset.voice.glideMs, 'preset');
      vs.unison.set(preset.voice.unison ?? 1, 'preset');
      vs.spread.set(preset.voice.unisonSpread ?? 15, 'preset');
    } else {
      const voices = this.deps.getVoiceManager();
      if (voices) {
        voices.setGlideMs(preset.voice.glideMs);
        voices.setMode(preset.voice.mode);
        voices.setPolyphony(preset.voice.polyphony);
        voices.setUnison(preset.voice.unison ?? 1);
        voices.setUnisonSpread(preset.voice.unisonSpread ?? 15);
      }
    }

    // Module params — match by type so preset ids don't need to line up
    // with runtime module ids. (They do with our deterministic ids today,
    // but robustness against future multi-module setups is free here.)
    const byType = new Map<string, SynthModule>();
    for (const m of this.deps.modules) byType.set(m.type, m);
    // First pass: any module whose type isn't in this preset and that opts
    // into resetToDefaults() gets cleared back to its default state. This
    // is how the arp cleanly turns off when the user loads a preset that
    // was saved without arp state (e.g. factory presets, pre-arp user
    // patches) — otherwise it'd linger from whatever was running.
    const presetTypes = new Set(preset.modules.map((s) => s.type));
    for (const m of this.deps.modules) {
      if (presetTypes.has(m.type)) continue;
      const resettable = m as unknown as { resetToDefaults?: () => void };
      resettable.resetToDefaults?.();
    }
    for (const state of preset.modules) {
      const m = byType.get(state.type);
      if (m) m.deserialize(state);
    }

    // Effect chain — rebuild the post-voice signal chain so the
    // composition (which effects exist, in what order, with what
    // params) matches the preset. Missing in pre-v4 presets that
    // somehow skipped the migration; in that case we leave the
    // runtime chain alone.
    if (this.deps.effectsChain && preset.effects) {
      this.deps.effectsChain.applyPreset(preset.effects);
    }

    // Macros — restore each value via the Parameter's subscribe chain so
    // the macro knobs update AND the ConstantSourceNode offsets follow.
    if (this.deps.macroBank && preset.macros) {
      const macros = this.deps.macroBank.macros;
      for (let i = 0; i < macros.length; i++) {
        const value = preset.macros[i];
        if (typeof value === 'number') {
          macros[i]!.value.set(value, 'preset');
        }
      }
    }

    // Mod connections — clear and rebuild. ModRouter's wireConnection is
    // tearDown-then-rewire at the per-connection level, so we don't need a
    // master-gain fade here.
    const router = this.deps.getRouter();
    if (router) {
      for (const existing of router.listConnections()) {
        router.removeConnection(existing.id);
      }
      for (const conn of preset.modConnections) {
        router.addConnection({
          sourceModuleId: conn.sourceModuleId,
          targetModuleId: conn.targetModuleId,
          targetParam: conn.targetParam,
          depth: conn.depth,
          // Preserve the optional fields when present so saved
          // patches restore identically. Missing fields default
          // (rangeMul=1, curve=linear) inside the router's
          // addConnection clamps.
          ...(conn.rangeMul !== undefined ? { rangeMul: conn.rangeMul } : {}),
          ...(conn.curve !== undefined ? { curve: conn.curve } : {}),
        });
      }
    }

    // MIDI Learn bindings — wholesale replace the runtime store with
    // whatever the preset captured. Empty array clears all bindings,
    // which is what users expect when loading a "clean" preset.
    if (this.deps.midiBindings && preset.midiBindings) {
      this.deps.midiBindings.applyBindings(preset.midiBindings);
    }
  }

  // ── Store ops ────────────────────────────────────────────────────────

  /** Save current state under `name`. Returns the new preset's id. */
  async save(
    name: string,
    opts: {
      author?: string;
      tags?: string[];
      /** Display category for the browser's grouped list. */
      category?: string;
    } = {},
  ): Promise<string> {
    const capture = this.captureState();
    const now = Date.now();
    const preset: Preset = {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      id: mintPresetId(),
      name,
      ...(opts.author !== undefined ? { author: opts.author } : {}),
      category: opts.category?.trim() || DEFAULT_PRESET_CATEGORY,
      createdAt: now,
      updatedAt: now,
      tags: opts.tags ?? [],
      ...capture,
    };
    await this.store.put(preset);
    this.emit();
    return preset.id;
  }

  /**
   * Overwrite an existing preset with the current state. By default keeps
   * the original name/category; pass `opts.name` / `opts.category` to
   * rename or re-categorize at the same time.
   */
  async update(
    id: string,
    opts: { name?: string; category?: string } = {},
  ): Promise<void> {
    const existing = await this.store.get(id);
    if (!existing) return;
    const capture = this.captureState();
    const next: Preset = {
      ...existing,
      ...capture,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      name: opts.name?.trim() || existing.name,
      category: opts.category?.trim() || existing.category,
      updatedAt: Date.now(),
    };
    await this.store.put(next);
    this.emit();
  }

  /** Load a preset into the live app. */
  async load(id: string): Promise<Preset | null> {
    const preset = await this.store.get(id);
    if (!preset) return null;
    this.applyState(preset);
    return preset;
  }

  async delete(id: string): Promise<void> {
    await this.store.delete(id);
    this.emit();
  }

  async list(): Promise<Preset[]> {
    return this.store.list();
  }

  subscribe(listener: PresetListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this);
  }
}

/** Re-export for callers that import this file directly. */
export type { Preset, ModConnection };
